import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, clearUser, setLoading } from '../store/slice/userSlice';

function toReduxUser(apiUser) {
  if (!apiUser) return null;
  const effectiveRole = apiUser.role || apiUser.clientRole || 'Viewer';
  return {
    isAuthenticated: true,
    id: apiUser.id || '',
    firstname: apiUser.firstname || apiUser.first_name || '',
    lastname: apiUser.lastname || apiUser.last_name || '',
    email: apiUser.email || '',
    mobile: apiUser.mobile || apiUser.phone || '',
    orgId: apiUser.orgId || apiUser.organization_id || '',
    organization_id: apiUser.organization_id || apiUser.orgId || '',
    clientRole: apiUser.clientRole || effectiveRole,
    role: effectiveRole,
  };
}

export function useAuth() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const reduxUser = useSelector((state) => state.user.user);
  const isLoadingFromStore = useSelector((state) => state.user.isLoading);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
  const queryKey = useMemo(() => [`${backendUrl}/api/auth/me`], [backendUrl]);

  const {
    data: apiUser,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        const res = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: 'include',
        });
        if (res.status === 401) return null;
        if (!res.ok) throw new Error('Failed to fetch session');
        return await res.json();
      } catch (err) {
        return null;
      }
    },
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Invalidate session cache when restored from browser bfcache (Back/Forward navigation)
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        queryClient.invalidateQueries({ queryKey });
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [queryClient, queryKey]);

  useEffect(() => {
    dispatch(setLoading(isLoading));
    if (isLoading) return;
    if (apiUser) {
      dispatch(setUser(toReduxUser(apiUser)));
    } else if (apiUser === null || isError) {
      dispatch(clearUser());
    }
  }, [apiUser, isLoading, isError, dispatch]);

  // If apiUser is explicitly null, we are definitely unauthenticated.
  const safeUser = apiUser ? toReduxUser(apiUser) : null;
  const effectiveUser = apiUser === null || isError ? null : (safeUser || reduxUser);

  // PropelX role helper compatibility
  const userRole = effectiveUser?.role || effectiveUser?.clientRole;
  const hasRole = (...roles) => userRole && roles.includes(userRole);
  const canManageUsers = () => hasRole('project_director', 'Admin');
  const canCreateProject = () => hasRole('project_director', 'project_manager', 'Admin');
  const canManageTasks = () => hasRole('project_director', 'project_manager', 'site_engineer', 'Admin');
  const isViewOnly = () => hasRole('client_rep', 'Viewer');

  return {
    user: effectiveUser,
    isLoading: isLoading || isLoadingFromStore,
    loading: isLoading || isLoadingFromStore,
    isAuthenticated: !!effectiveUser?.isAuthenticated,
    role: userRole,
    hasRole,
    canManageUsers,
    canCreateProject,
    canManageTasks,
    isViewOnly,
  };
}

export default useAuth;
